'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage, isCaptchaValidationError } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { GdprTextNewsletter } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-newsletter';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { IoMdPaperPlane } from 'react-icons/io';
import { LuMail } from 'react-icons/lu';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const generalLayout = useGlobalStore((s) => s.generalLayout);

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => {
      setIsSubscribed(true);
      trackFormSubmitSuccess({
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: 2,
        send_to: generalLayout.marketing?.gtm_id,
      });
    },
    onError: (captchaRequestError) => { if (isCaptchaValidationError(captchaRequestError)) { setIsAlreadySubscribed(false); return; } setIsAlreadySubscribed(true); },
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col items-start justify-start gap-3">
<CaptchaRequestMessage error={subscribeMutation.error} />
      <h2 data-ea-visual-tag="h5" className="text-base font-bold text-white sm:text-lg lg:text-xl">Newsletter</h2>
      <Input
        aria-label={systemLabels('email')}
        id={`newsletter-email-${seoNewsletterId}`}
        type="email"
        name="email"
        placeholder={'Zadajte váš e-mail'}
        onChange={(e) => setEmail(e.target.value)}
        className={{
          wrapper: cn(
            'h-[58px] w-full rounded-full border border-white/15 bg-white/[0.08]',
            'ea-homepage-newsletter__form-item-email-input',
          ),
          main: cn(
            'h-[58px] w-full rounded-full border-none bg-transparent px-6 text-base font-normal text-white placeholder:text-[#99B299] sm:text-lg',
          ),
        }}
        after={
          <Button
            type="submit"
            aria-label={t('text182').trim() || 'Prihlásiť sa na odber noviniek'}
            className={cn(
              'h-[50px] w-[50px] rounded-full bg-white p-0 text-secondary sm:w-fit sm:px-6',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
            disabled={email.length === 0 || !checked || isSubscribed}
            isLoading={subscribeMutation.isPending}
          >
            <IoMdPaperPlane size={20} className="block text-secondary sm:hidden" />
            <p className="hidden sm:block">{t('text182')}</p>
          </Button>
        }
      />
      <p className="text-xs text-white/70">
        Prihlásením na odber noviniek súhlasíte so spracovaním osobných údajov pre účely
        kontaktovania a marketingu.
      </p>
    </form>
  );
}
